/**
 * Phase 6: hands-on dice. A tiny 2D physics model for the dice tray: the player's flick sets the velocity, dice
 * bounce off the walls and each other, spin, and slow down. Purely cosmetic: the SERVER decides the numbers.
 */
export const DICE_PHYSICS = {
    /** Fraction of speed kept after hitting a wall. */
    restitution: 0.55,
    /** Speed kept per second rolling on the felt (exponential). */
    friction: 0.18,
    spinFriction: 0.12,
    /** Below this (px/s and rad/s) a die is at rest. */
    restSpeed: 12,
    restSpin: 0.4,
    maxSpeed: 2600,
};
/** Clamp a flick to a sensible throw; a tiny flick gets a gentle random toss. */
export function throwVelocity(vx, vy, rand = Math.random) {
    const speed = Math.hypot(vx, vy);
    if (!Number.isFinite(speed) || speed < 150) {
        const ang = rand() * Math.PI * 2;
        const s = 700 + rand() * 600;
        return { vx: Math.cos(ang) * s, vy: Math.sin(ang) * s };
    }
    const k = Math.min(1, DICE_PHYSICS.maxSpeed / speed);
    return { vx: vx * k, vy: vy * k };
}
/** Advance the simulation by dt seconds inside a w×h tray. Returns true while anything is still moving. */
export function stepDice(bodies, w, h, dt) {
    const P = DICE_PHYSICS;
    const fr = Math.pow(P.friction, dt);
    const sf = Math.pow(P.spinFriction, dt);
    let moving = false;
    for (const b of bodies) {
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.a += b.va * dt;
        if (b.x - b.r < 0) {
            b.x = b.r;
            b.vx = Math.abs(b.vx) * P.restitution;
            b.va += b.vy * 0.004;
        }
        if (b.x + b.r > w) {
            b.x = w - b.r;
            b.vx = -Math.abs(b.vx) * P.restitution;
            b.va -= b.vy * 0.004;
        }
        if (b.y - b.r < 0) {
            b.y = b.r;
            b.vy = Math.abs(b.vy) * P.restitution;
            b.va -= b.vx * 0.004;
        }
        if (b.y + b.r > h) {
            b.y = h - b.r;
            b.vy = -Math.abs(b.vy) * P.restitution;
            b.va += b.vx * 0.004;
        }
        b.vx *= fr;
        b.vy *= fr;
        b.va *= sf;
    }
    // Dice knock into each other (equal mass, elastic-ish).
    for (let i = 0; i < bodies.length; i++) {
        for (let j = i + 1; j < bodies.length; j++) {
            const p = bodies[i];
            const q = bodies[j];
            const dx = q.x - p.x;
            const dy = q.y - p.y;
            const d = Math.hypot(dx, dy) || 0.001;
            const min = p.r + q.r;
            if (d >= min)
                continue;
            const nx = dx / d;
            const ny = dy / d;
            const push = (min - d) / 2;
            p.x -= nx * push;
            p.y -= ny * push;
            q.x += nx * push;
            q.y += ny * push;
            const rel = (q.vx - p.vx) * nx + (q.vy - p.vy) * ny;
            if (rel < 0) {
                const imp = -rel * (1 + P.restitution) / 2;
                p.vx -= imp * nx;
                p.vy -= imp * ny;
                q.vx += imp * nx;
                q.vy += imp * ny;
                p.va += imp * 0.01;
                q.va -= imp * 0.01;
            }
        }
    }
    for (const b of bodies) {
        // Collisions may have nudged a die into a wall.
        b.x = Math.min(w - b.r, Math.max(b.r, b.x));
        b.y = Math.min(h - b.r, Math.max(b.r, b.y));
        if (Math.hypot(b.vx, b.vy) < P.restSpeed && Math.abs(b.va) < P.restSpin) {
            b.vx = 0;
            b.vy = 0;
            b.va = 0;
        }
        else {
            moving = true;
        }
    }
    return moving;
}
//# sourceMappingURL=dicephysics.js.map